/**
 * LoginGate — modern dark-glass login screen.
 *
 * Renders children when authenticated, a centred login card otherwise.
 * State machine intentionally minimal: a single boolean `authed` driven
 * by webAuthClient.isAuthenticated().  Login success flips the gate;
 * logout (handled elsewhere) reloads the page so this remounts.
 */

import { FormEvent, ReactNode, useState } from 'react'
import { isAuthenticated, login } from './webAuthClient'

interface LoginGateProps {
  children: ReactNode
}

/** Gontec brand green (from the logo). Reused for focus rings + the submit button. */
const BRAND = '#0d5d2a'
const BRAND_HOVER = '#0f6e30'

export function LoginGate({ children }: LoginGateProps) {
  const [authed, setAuthed] = useState<boolean>(isAuthenticated)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (authed) {
    return <>{children}</>
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(email.trim(), password)
      setAuthed(true)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={pageStyle}>
      {/* Subtle ambient blobs — pure CSS, no JS animation cost */}
      <div style={blobOneStyle} aria-hidden />
      <div style={blobTwoStyle} aria-hidden />

      <form onSubmit={onSubmit} style={cardStyle} autoComplete="on">
        <div style={brandHeaderStyle}>
          <div style={logoFrameStyle}>
            <img
              src="/gontec-logo.jpg"
              alt="Gontec-MV"
              draggable={false}
              style={{ width: 64, height: 'auto', display: 'block', borderRadius: 4 }}
            />
          </div>
          <h1 style={titleStyle}>3D Lite</h1>
        </div>

        <Field label="E-Mail">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            autoComplete="email"
            placeholder="name@gontec-mv.de"
            style={inputStyle}
            onFocus={setFocusRing}
            onBlur={clearFocusRing}
          />
        </Field>

        <Field label="Passwort">
          <div style={{ position: 'relative' }}>
            <input
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={{ ...inputStyle, paddingRight: 64 }}
              onFocus={setFocusRing}
              onBlur={clearFocusRing}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              style={pwToggleStyle}
              tabIndex={-1}
            >
              {showPw ? 'Verbergen' : 'Zeigen'}
            </button>
          </div>
        </Field>

        {error && <div style={errorStyle}>{error}</div>}

        <button type="submit" disabled={busy} style={submitStyle(busy)}>
          {busy ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Spinner /> Wird angemeldet…
            </span>
          ) : (
            'Anmelden'
          )}
        </button>
      </form>
    </div>
  )
}

// ─── Small subcomponents ────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      {children}
    </label>
  )
}

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: '2px solid rgba(255,255,255,0.35)',
        borderTopColor: '#fff',
        display: 'inline-block',
        animation: 'rl-spin 0.7s linear infinite',
      }}
    />
  )
}

// ─── Focus-ring micro behaviour (no React state — DOM only, zero rerenders) ─

function setFocusRing(e: React.FocusEvent<HTMLInputElement>) {
  e.currentTarget.style.borderColor = BRAND
  e.currentTarget.style.boxShadow = `0 0 0 3px rgba(13,93,42,0.25)`
}
function clearFocusRing(e: React.FocusEvent<HTMLInputElement>) {
  e.currentTarget.style.borderColor = '#2a2a2a'
  e.currentTarget.style.boxShadow = 'none'
}

// ─── Styles (inline so this stays a single-file component) ──────────────────

const pageStyle: React.CSSProperties = {
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  background:
    'radial-gradient(1200px 600px at 20% -10%, rgba(13,93,42,0.18), transparent 60%),' +
    'radial-gradient(900px 500px at 100% 110%, rgba(255,212,0,0.06), transparent 55%),' +
    '#0e1013',
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  color: '#e7e9ec',
  overflow: 'hidden',
}

const blobOneStyle: React.CSSProperties = {
  position: 'absolute',
  top: '-160px',
  left: '-120px',
  width: 460,
  height: 460,
  background: 'radial-gradient(circle, rgba(13,93,42,0.35), transparent 65%)',
  filter: 'blur(40px)',
  pointerEvents: 'none',
}

const blobTwoStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '-200px',
  right: '-160px',
  width: 520,
  height: 520,
  background: 'radial-gradient(circle, rgba(255,212,0,0.10), transparent 65%)',
  filter: 'blur(50px)',
  pointerEvents: 'none',
}

const cardStyle: React.CSSProperties = {
  position: 'relative',
  width: 380,
  maxWidth: 'calc(100% - 2rem)',
  padding: '2rem 1.75rem 1.5rem',
  background: 'rgba(20,22,26,0.78)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  boxShadow:
    '0 30px 60px -20px rgba(0,0,0,0.7), 0 8px 24px -12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)',
  backdropFilter: 'blur(14px) saturate(140%)',
  WebkitBackdropFilter: 'blur(14px) saturate(140%)',
}

const brandHeaderStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  marginBottom: 22,
}

const logoFrameStyle: React.CSSProperties = {
  width: 72,
  height: 72,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 14,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
}

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 22,
  fontWeight: 600,
  letterSpacing: 0.3,
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  opacity: 0.6,
  marginBottom: 6,
  fontWeight: 500,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 12px',
  background: 'rgba(255,255,255,0.04)',
  color: '#f2f3f5',
  border: '1px solid #2a2a2a',
  borderRadius: 10,
  fontFamily: 'inherit',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 120ms ease, box-shadow 120ms ease, background 120ms ease',
}

const pwToggleStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  right: 8,
  transform: 'translateY(-50%)',
  padding: '4px 8px',
  background: 'transparent',
  border: 'none',
  color: '#9aa1a8',
  fontSize: 11,
  letterSpacing: 0.5,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const errorStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: 'rgba(239,68,68,0.10)',
  border: '1px solid rgba(239,68,68,0.35)',
  borderRadius: 10,
  marginBottom: 14,
  fontSize: 13,
  color: '#fca5a5',
  lineHeight: 1.4,
}

const submitStyle = (busy: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '12px',
  background: busy ? '#2a2a2a' : BRAND,
  color: busy ? '#888' : '#fff',
  border: 'none',
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: 0.3,
  cursor: busy ? 'wait' : 'pointer',
  fontFamily: 'inherit',
  transition: 'background 120ms ease, transform 100ms ease',
  marginTop: 4,
  // Hover handled by inline event listeners on the button — but inline styles
  // can't express :hover, so we rely on the base look being good enough.
  // (We give a small tactile press via active state below via onPointerDown.)
  boxShadow: busy ? 'none' : '0 6px 18px -6px rgba(13,93,42,0.55)',
})

// Inject keyframes once for the spinner.
if (typeof document !== 'undefined' && !document.getElementById('rl-keyframes')) {
  const style = document.createElement('style')
  style.id = 'rl-keyframes'
  style.textContent =
    '@keyframes rl-spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }'
  document.head.appendChild(style)
}

// Suppress the "BRAND_HOVER unused" warning — kept for future hover wiring.
void BRAND_HOVER
